/**
 * Which state the LLM-profiles ADVISORY block is in.
 *
 * Consumer half of `ai-docs/lexportal-llm-advisories-miniplan.md` (handshake
 * `cp7s`, agreed `lq46`). Pure so the four states can be pinned by test without
 * rendering — the same shape as `selectConvergenceState`.
 *
 * 🔑 The distinction this exists to protect is **unknown vs. empty**. "This
 * catalog raised no advisories" and "the catalog could not be read, so none
 * could be computed" are different facts, and only one of them means stop
 * looking. Collapsing the second into the first shows a clean bill of health
 * for a platform nobody managed to inspect — a defect class this repo has paid
 * for repeatedly, including twice in this same arc in one week.
 *
 * ⚖ `hidden` is a DELIBERATE decision, endorsed by `lexide-im` in the handoff,
 * and is NOT a violation of that rule. When the catalog is absent the page
 * already renders a loud red three-state banner that explains the situation
 * far more completely than `status:"unknown"` would AND offers the recovery
 * action. A second, quieter "advisories could not be computed" underneath
 * competes with that recovery while adding nothing. What is suppressed is a
 * redundant second telling of a fact the page already states — not a state.
 *
 * ⚠ The residual case is exactly why `unknown` still exists below: an
 * `unknown` while the catalog IS present (unreadable-but-existing) renders,
 * because nothing else on the page explains that one.
 */

import type { CatalogAdvisory, CatalogAdvisorySet } from "@/lib/platform-api";

export interface AdvisoryStateInputs {
  /** Whether a catalog is present. When false the absent-banner owns the page. */
  catalogPresent: boolean;
  /** The advisory read. `null` = not (yet) read, or the read failed. */
  set: CatalogAdvisorySet | null;
}

export type AdvisoryState =
  /** Catalog absent — the red recovery banner explains it; render nothing here. */
  | { kind: "hidden" }
  /** Read failed or not yet returned — say so; never imply a clean catalog. */
  | { kind: "unreadable" }
  /** Server could not compute — render `reason`; never an empty state. */
  | { kind: "unknown"; reason: string }
  /** Computed, nothing to report — may render nothing at all. */
  | { kind: "none" }
  /** Computed, with observations. */
  | { kind: "rows"; advisories: CatalogAdvisorySet & { status: "known" } };

export function selectAdvisoryState(inputs: AdvisoryStateInputs): AdvisoryState {
  // Order matters: absent-catalog wins before anything is said about advisories,
  // because the banner is a better answer to the same question.
  if (!inputs.catalogPresent) return { kind: "hidden" };

  // A failed/absent READ is not a clean catalog. Distinct from `unknown`:
  // `unknown` means the server answered and could not compute; `unreadable`
  // means we never got an answer. Both must be visible, and they are different.
  if (inputs.set === null) return { kind: "unreadable" };

  if (inputs.set.status === "unknown") {
    return { kind: "unknown", reason: inputs.set.reason };
  }

  if (inputs.set.advisories.length === 0) return { kind: "none" };

  return { kind: "rows", advisories: inputs.set };
}

/** Emphasis for one row — a function of `code` ONLY.
 *
 * ⚠ NEVER branch on `statement`/`consequence` text. lexserver regex-tests its
 * prose to keep it descriptive; styling read from that prose would silently
 * re-verdict every future row they add. `DANGLING` is the one row lexserver
 * sanctions as a verdict ("not a matter of intent"), so it alone is emphatic.
 */
export function advisoryEmphasis(
  code: CatalogAdvisory["code"],
): "verdict" | "neutral" {
  return code === "DEFAULT_CHAT_PROFILE_DANGLING" ? "verdict" : "neutral";
}

/**
 * The `If-Match` precondition token for a catalog save — the `setAt` the draft
 * on screen was loaded from.
 *
 * 🚨 Returning `undefined` is NOT a safe default. lexserver treats an absent
 * `If-Match` as "no precondition" and falls back to LAST-WRITE-WINS, which is
 * the exact hazard the guard exists to close: this editor seeds its draft at
 * mount, so a tab left open across someone else's save silently reverts them.
 * `undefined` is correct in EXACTLY ONE case — no catalog is stored, so there
 * is nothing to be stale against and a precondition would be meaningless.
 *
 * ⚠ The token rides a HEADER. It must never be folded into the POST body:
 * `POST /llm-profiles` strict-parses the body and would reject it as
 * `unrecognized_keys`. That mistake was proposed by this lane and caught in
 * review — see `lexiun-llm-providers-guide.md` §7.1-nonies.
 */
export function ifMatchToken(
  current: { setAt: string } | null,
): string | undefined {
  if (!current) return undefined;
  return new Date(current.setAt).toISOString();
}

/**
 * Would saving this draft be a NO-OP against what was loaded?
 *
 * 🚨 Why this exists: every write to the platform catalog ROTATES `/previous`,
 * and there is exactly ONE generation of undo. So a Save with no edits is not
 * harmless — it advances `setAt` and replaces the real prior generation with a
 * copy of the current one, **destroying the recovery slot while changing
 * nothing.** That is not hypothetical: `lexserver`'s live probes on 2026-08-31
 * POSTed the catalog back verbatim three times and consumed the pre-flip
 * catalog that had been described as "one restore away".
 *
 * The operator instinct this protects against is the reasonable one — clicking
 * Save to be sure, especially during an incident, on the exact page where the
 * undo slot is most likely to matter.
 *
 * ⚠ Compared as CANONICAL JSON, not as raw text: reformatting or re-ordering
 * keys in the textarea is not an edit, and treating it as one would burn the
 * slot for a cosmetic change. Conversely any real value difference — including
 * a removed profile — compares unequal and saves normally.
 */
export function isNoOpSave(
  draft: unknown,
  loaded: unknown | null,
): boolean {
  if (loaded === null || loaded === undefined) return false;
  return canonical(draft) === canonical(loaded);
}

/** Stable stringify — object keys sorted at every depth, so key ORDER never
 *  reads as a difference. Arrays keep their order, which IS meaningful. */
function canonical(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value) ?? "";
  if (Array.isArray(value)) return `[${value.map(canonical).join(",")}]`;
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonical(v)}`).join(",")}}`;
}
